/**
 * How a project shows its reports (2026-10-25): TABS of one page, with one
 * set of filters, or a LIST in the portal sidebar, each report with its
 * own filters. Binary and chosen per project (decision of Pablo); the
 * mode is derived on the server from the project's report group.
 *
 * Switching is two steps: «Review the change» runs the server's dry run
 * (the same writes and Publish checks, rolled back) and lists what will
 * happen; «Confirm» applies it. A switch that would break a published
 * report lists the errors and cannot be confirmed. A «mixed» project
 * (a group next to loose reports, from before the rule) offers no
 * switch: its reports have to be moved to another project first.
 */
import { Button } from "@omnitok/ui";
import { Callout, Section } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Check, Layers, List } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useT } from "@/i18n";
import {
  projectModeApi,
  type ProjectModeChange,
  type ProjectModeState,
} from "@/lib/project-mode-api";
import { errorLines } from "@/pages/projects/ReportGroupPage";
import type { Project } from "@/types";

type Target = "tabs" | "list";

interface ProjectModeCardProps {
  project: Project;
  /** After an applied switch: the hub re-reads its reports. */
  onChanged: () => void;
}

export function ProjectModeCard({ project, onChanged }: ProjectModeCardProps): JSX.Element {
  const { t } = useT();
  const [state, setState] = useState<ProjectModeState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [review, setReview] = useState<ProjectModeChange | null>(null);
  const [busy, setBusy] = useState<"review" | "apply" | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setState(await projectModeApi.get(project.slug));
    } catch (err) {
      setLoadError(errorLines(err).join(" — "));
    }
  }, [project.slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const runReview = async (target: Target) => {
    setBusy("review");
    setErrors([]);
    setNotice(null);
    try {
      setReview(await projectModeApi.switch(project.slug, target, false));
    } catch (err) {
      setReview(null);
      setErrors(errorLines(err));
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    if (!review) return;
    setBusy("apply");
    setErrors([]);
    try {
      await projectModeApi.switch(project.slug, review.target, true);
      setNotice(review.target === "tabs" ? t("projectMode.appliedTabs") : t("projectMode.appliedList"));
      setReview(null);
      await load();
      onChanged();
    } catch (err) {
      setErrors(errorLines(err));
    } finally {
      setBusy(null);
    }
  };

  const options: { id: Target; icon: ReactNode; title: string; body: string }[] = [
    {
      id: "tabs",
      icon: <Layers size={16} aria-hidden />,
      title: t("projectMode.tabs"),
      body: t("projectMode.tabsHint"),
    },
    {
      id: "list",
      icon: <List size={16} aria-hidden />,
      title: t("projectMode.list"),
      body: t("projectMode.listHint"),
    },
  ];

  return (
    <Section title={t("projectMode.title")} description={t("projectMode.description")}>
      {loadError && (
        <Callout tone="danger" title={t("projectMode.loadError")}>
          {loadError}
        </Callout>
      )}
      {!state && !loadError && <Spinner size={16} ariaLabel={t("common.loading")} />}

      {state?.mode === "mixed" && (
        <Callout tone="warning" title={t("projectMode.mixedTitle")}>
          <p>{t("projectMode.mixedBody")}</p>
          <p className="mt-1 font-mono text-xs">
            {t("projectMode.mixedGroups")}: {state.groups.join(", ")} · {t("projectMode.mixedLoose")}:{" "}
            {state.loose.join(", ")}
          </p>
        </Callout>
      )}

      {state && state.mode !== "mixed" && (
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t("projectMode.title")}>
          {options.map((opt) => {
            const current = state.mode === opt.id;
            return (
              <div
                key={opt.id}
                role="radio"
                aria-checked={current}
                data-project-mode={opt.id}
                className={
                  "rounded-lg border p-3 text-sm " +
                  (current ? "border-primary bg-primary-soft ring-1 ring-primary" : "border-neutral-200 bg-white")
                }
              >
                <div className="flex items-center gap-2 font-semibold text-neutral-900">
                  {opt.icon}
                  <span className="flex-1">{opt.title}</span>
                  {current && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                      <Check size={12} aria-hidden /> {t("projectMode.current")}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[13px] text-neutral-600">{opt.body}</p>
                {current && opt.id === "tabs" && state.group_key && (
                  <Link
                    to={`/projects/${encodeURIComponent(project.slug)}/groups/${encodeURIComponent(state.group_key)}`}
                    className="mt-2 inline-block text-xs font-semibold text-primary hover:underline"
                  >
                    {t("projectMode.openGroup")}
                  </Link>
                )}
                {!current && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={busy !== null}
                    onClick={() => void runReview(opt.id)}
                  >
                    {busy === "review" ? t("projectMode.reviewing") : t("projectMode.review")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {review && <ReviewPanel review={review} busy={busy} onApply={() => void apply()} onCancel={() => setReview(null)} />}

      {errors.length > 0 && (
        <Callout tone="danger" title={errors[0]!} className="mt-3">
          <ul className="list-disc pl-5">
            {errors.slice(1).map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Callout>
      )}
      {notice && errors.length === 0 && (
        <p role="status" className="mt-3 text-xs font-medium text-emerald-700">
          {notice}
        </p>
      )}
    </Section>
  );
}

export function ReviewPanel({
  review,
  busy,
  onApply,
  onCancel,
}: {
  review: ProjectModeChange;
  busy: "review" | "apply" | null;
  onApply: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useT();
  const toTabs = review.target === "tabs";
  const notApplicable = Object.entries(review.not_applicable);
  const lines: string[] = toTabs
    ? [
        t("projectMode.r.tabsReports", { count: review.reports.length, group: review.group_key ?? "" }),
        t("projectMode.r.tabsFilters", { count: review.filters.length, filters: review.filters.join(", ") }),
        ...notApplicable.map(([report, keys]) => t("projectMode.r.notApplicable", { report, filters: keys.join(", ") })),
        ...(review.saved_views ? [t("projectMode.r.savedViewsUp", { count: review.saved_views })] : []),
      ]
    : [
        t("projectMode.r.listReports", { count: review.reports.length }),
        ...review.removed_actions.map((a) => t("projectMode.r.removedAction", { action: a })),
        ...(review.saved_views ? [t("projectMode.r.savedViewsDown", { count: review.saved_views })] : []),
        ...review.trimmed.map((x) => t("projectMode.r.trimmed", { item: x })),
        ...(review.lost_aliases.length
          ? [t("projectMode.r.lostAliases", { tabs: review.lost_aliases.join(", ") })]
          : []),
      ];
  return (
    <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3" data-project-mode-review="">
      <p className="text-sm font-semibold text-neutral-900">
        {toTabs ? t("projectMode.reviewTitleTabs") : t("projectMode.reviewTitleList")}
      </p>
      <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[13px] text-neutral-700">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {review.errors.length > 0 && (
        <Callout tone="danger" title={t("projectMode.reviewBlocked")} className="mt-3">
          <ul className="list-disc pl-5">
            {review.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Callout>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={busy !== null} onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button size="sm" disabled={busy !== null || review.errors.length > 0} onClick={onApply}>
          {busy === "apply" ? t("projectMode.applying") : t("projectMode.confirm")}
        </Button>
      </div>
    </div>
  );
}
